/**
 * AdminRequestsPanel - Super Admin panel for reviewing admin access requests
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { adminRequestAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Clock, Check, X, User, Mail, Phone, Building2, Calendar,
  Users, FileText, Loader2, AlertTriangle, CheckCircle2, XCircle,
  Eye, Copy, RefreshCw, Search, Filter
} from 'lucide-react';

const AdminRequestsPanel = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('EventFlow2024!');
  const [rejectReason, setRejectReason] = useState('');

  // Fetch requests
  const { data: requests, isLoading, refetch } = useQuery({
    queryKey: ['admin-requests', statusFilter],
    queryFn: () => adminRequestAPI.list(statusFilter === 'all' ? null : statusFilter).then(r => r.data),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: ({ requestId, data }) => adminRequestAPI.approve(requestId, data),
    onSuccess: (data) => {
      toast.success(`Admin account created for ${selectedRequest?.email}`);
      queryClient.invalidateQueries(['admin-requests']);
      setShowApproveDialog(false);
      setSelectedRequest(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    }
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ requestId, data }) => adminRequestAPI.reject(requestId, data),
    onSuccess: () => {
      toast.success('Request rejected');
      queryClient.invalidateQueries(['admin-requests']);
      setShowRejectDialog(false);
      setSelectedRequest(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    }
  });

  const handleApprove = () => {
    approveMutation.mutate({
      requestId: selectedRequest.id,
      data: { temp_password: tempPassword }
    });
  };

  const handleReject = () => {
    rejectMutation.mutate({
      requestId: selectedRequest.id,
      data: { reason: rejectReason }
    });
  };

  // Filter requests by search
  const filteredRequests = requests?.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.email?.toLowerCase().includes(query) ||
      req.first_name?.toLowerCase().includes(query) ||
      req.last_name?.toLowerCase().includes(query) ||
      req.event_name?.toLowerCase().includes(query) ||
      req.company?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-500/20 text-emerald-400"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = requests?.filter(r => r.status === 'pending').length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Admin Requests</h2>
          <p className="text-slate-500">Review and approve admin access requests</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            You have <strong>{pendingCount}</strong> pending request{pendingCount > 1 ? 's' : ''} awaiting review.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="flex-1">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending {pendingCount > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search requests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-requests"
          />
        </div>
      </div>

      {/* Request List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredRequests?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No requests found</p>
            {statusFilter === 'pending' && (
              <p className="text-sm text-slate-400 mt-1">All caught up! No pending requests to review.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests?.map((request) => (
            <Card key={request.id} className="hover:shadow-md transition-shadow" data-testid={`request-${request.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Avatar */}
                  <Avatar className="h-12 w-12 flex-shrink-0">
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 font-semibold">
                      {request.first_name?.[0]}{request.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">
                        {request.first_name} {request.last_name}
                      </h3>
                      {getStatusBadge(request.status)}
                    </div>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mb-2">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />
                        {request.email}
                      </span>
                      {request.company && (
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5" />
                          {request.company}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <span className="flex items-center gap-1 text-indigo-600">
                        <Calendar className="h-3.5 w-3.5" />
                        {request.event_name}
                      </span>
                      <span className="flex items-center gap-1 text-slate-500">
                        <Users className="h-3.5 w-3.5" />
                        {request.expected_attendees}+ attendees
                      </span>
                    </div>
                    
                    <p className="text-xs text-slate-400 mt-2">
                      Submitted {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex sm:flex-col gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedRequest(request)}
                      data-testid={`view-request-${request.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                    {request.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowApproveDialog(true);
                          }}
                          data-testid={`approve-${request.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowRejectDialog(true);
                          }}
                          data-testid={`reject-${request.id}`}
                        >
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Request Details Dialog */}
      <Dialog open={!!selectedRequest && !showApproveDialog && !showRejectDialog} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              Full details of the admin access request
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-6 p-1">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Status</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                
                {/* Personal Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" /> Personal Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">Name</span>
                      <p className="font-medium">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Email</span>
                      <p className="font-medium">{selectedRequest.email}</p>
                    </div>
                    {selectedRequest.phone && (
                      <div>
                        <span className="text-slate-500">Phone</span>
                        <p className="font-medium">{selectedRequest.phone}</p>
                      </div>
                    )}
                    {selectedRequest.company && (
                      <div>
                        <span className="text-slate-500">Company</span>
                        <p className="font-medium">{selectedRequest.company}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Event Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> Event Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">Event Name</span>
                      <p className="font-medium">{selectedRequest.event_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Expected Attendees</span>
                      <p className="font-medium">{selectedRequest.expected_attendees}+</p>
                    </div>
                    {selectedRequest.event_date && (
                      <div>
                        <span className="text-slate-500">Event Date</span>
                        <p className="font-medium">{new Date(selectedRequest.event_date).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                  {selectedRequest.event_description && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <span className="text-slate-500 text-sm">Description</span>
                      <p className="text-sm mt-1">{selectedRequest.event_description}</p>
                    </div>
                  )}
                </div>
                
                {/* Reason */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 mb-2">Request Reason</h4>
                  <p className="text-sm">{selectedRequest.reason}</p>
                </div>
                
                {/* Review Notes (if reviewed) */}
                {selectedRequest.review_notes && (
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-700 mb-2">Review Notes</h4>
                    <p className="text-sm text-amber-800">{selectedRequest.review_notes}</p>
                    {selectedRequest.reviewed_at && (
                      <p className="text-xs text-amber-600 mt-2">
                        Reviewed on {new Date(selectedRequest.reviewed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Request Metadata */}
                <div className="text-xs text-slate-400 space-y-1">
                  <p>Request ID: <code className="bg-slate-100 px-1 rounded">{selectedRequest.id}</code></p>
                  <p>Submitted: {new Date(selectedRequest.created_at).toLocaleString()}</p>
                </div>
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowRejectDialog(true);
                  }}
                >
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    setShowApproveDialog(true);
                  }}
                >
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-5 w-5" /> Approve Request
            </DialogTitle>
            <DialogDescription>
              This will create an admin account for {selectedRequest?.email} and set up their event.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-sm text-blue-800">
                <strong>What will happen:</strong>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>An admin account will be created with the email {selectedRequest?.email}</li>
                  <li>A new event &quot;{selectedRequest?.event_name}&quot; will be created</li>
                  <li>The user will be assigned as Event Admin for that event</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Temporary Password</Label>
              <div className="flex gap-2">
                <Input
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  data-testid="temp-password"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigator.clipboard.writeText(tempPassword)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                The user should change this password after first login
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleApprove}
              disabled={approveMutation.isPending}
              data-testid="confirm-approve"
            >
              {approveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Approve & Create Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" /> Reject Request
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this request. The user will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please explain why this request is being rejected..."
                className="min-h-[100px]"
                data-testid="reject-reason"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending || !rejectReason}
              data-testid="confirm-reject"
            >
              {rejectMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Reject Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRequestsPanel;
